import { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

import FieldHistory, { FieldChange } from './FieldHistory';
import { ValueConfidence } from './valueConfidence';

/**
 * One row of a perspective form: history, label, control, unit.
 *
 * The state dot comes first and is always there — green, amber or red. It used to be a history
 * icon that appeared only where a history existed, which marked the wrong half: a field with no
 * value and no history is the one most worth flagging, and it showed nothing. Keeping the slot at a
 * fixed width means only the colours differ between rows, which is exactly the difference worth
 * seeing when the eye runs down the column.
 *
 * Narrow viewports stack the row instead of squeezing the control: at 360 px a 220 px input beside
 * a label leaves neither of them readable.
 */

interface Properties {
    label: string;
    unit?: string;
    changes: FieldChange[];
    confidence: ValueConfidence;
    /** How a stored value is written in the history list. */
    format: (value: any) => string;
    onRestore: (value: any) => void;
    disabled?: boolean;
    /** This field is on its way to the server. */
    saving?: boolean;
    /** Listen, Objekte und gebundene Instanzen brauchen die ganze Breite unter der Beschriftung. */
    wide?: boolean;
    children: ReactNode;
}

export default function PerspectiveFormRow({
    label,
    unit,
    changes,
    confidence,
    format,
    onRestore,
    disabled,
    saving,
    wide,
    children,
}: Properties) {
    return (
        <div className="grid grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1 border-b py-2 sm:grid-cols-[2.5rem_minmax(0,1fr)_220px_max-content]">
            <span className="inline-flex w-10 items-center justify-center">
                {/* In the dot's place while the field is being written: it is the dot that is about
                    to change, and the reader is waiting on exactly that. */}
                {saving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" role="status" />
                ) : (
                <FieldHistory
                    changes={changes}
                    confidence={confidence}
                    format={format}
                    onRestore={onRestore}
                    disabled={disabled}
                />
                )}
            </span>
            <span className="min-w-0 text-sm leading-snug">{label}</span>
            <div className={wide ? 'col-start-2 min-w-0 sm:col-span-3 sm:col-start-2' : 'col-start-2 min-w-0 sm:col-start-3'}>{children}</div>
            <span className="col-start-2 text-xs text-muted-foreground sm:col-start-4 sm:min-w-[56px]">
                {unit ?? ''}
            </span>
        </div>
    );
}
